import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, concatMap, take } from 'rxjs';

import {
  ClientRegisteredVendor,
  ExemptVendor,
  Vendor
} from '../classes/models';
import { CompanyComplianceType } from '../classes/models/company-compliance-type';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';

@Injectable({
  providedIn: 'root'
})
export class ClientRegisteredVendorService {
  constructor(
    private http: HttpClient,
    private AuthService: AuthService,
    private ToastService: ToastService
  ) {}

  public getClientRegisteredVendor(
    query: {} = {}
  ): Observable<ClientRegisteredVendor> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get<ClientRegisteredVendor>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/clientRegisteredVendor/one`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<ClientRegisteredVendor>(
              `/api/v2/admin/clientRegisteredVendor/one`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public getClientRegisteredVendors(
    query: {} = {}
  ): Observable<ClientRegisteredVendor[]> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get<ClientRegisteredVendor[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/clientRegisteredVendor/many`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<ClientRegisteredVendor[]>(
              `/api/v2/admin/clientRegisteredVendor/many`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public convertClientRegisteredVendorToComplianceExempt(
    query: {} = {}
  ): Observable<ExemptVendor> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.get<ExemptVendor>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/clientRegisteredVendor/convertVendorToExemptVendor`,
              { params: query }
            ),
          Admin: () =>
            this.http.get<ExemptVendor>(
              `/api/v2/admin/clientRegisteredVendor/convertVendorToExemptVendor`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public checkForDuplicateTaxId(
    ClientRegisteredVendorRecord: ClientRegisteredVendor,
    query: {} = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post<ClientRegisteredVendor[]>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/clientRegisteredVendor/checkForDuplicateTaxId`,
              ClientRegisteredVendorRecord,
              { params: query }
            ),
          Admin: () =>
            this.http.post<ClientRegisteredVendor[]>(
              `/api/v2/admin/clientRegisteredVendor/checkForDuplicateTaxId`,
              ClientRegisteredVendorRecord,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public saveClientRegisteredVendorHandleCompliance(
    ClientRegisteredVendorRecord: ClientRegisteredVendor,
    CompanyComplianceTypeId: CompanyComplianceType['id'],
    query: {} = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/clientRegisteredVendor/${CompanyComplianceTypeId}/saveClientRegisteredVendorHandleCompliance`,
              ClientRegisteredVendorRecord,
              { params: query }
            ),
          Admin: () =>
            this.http.post(
              `/api/v2/admin/clientRegisteredVendor/${CompanyComplianceTypeId}/saveClientRegisteredVendorHandleCompliance`,
              ClientRegisteredVendorRecord,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public saveClientRegisteredVendor(
    ClientRegisteredVendorRecord: ClientRegisteredVendor | {},
    query: {} = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/clientRegisteredVendor/one`,
              ClientRegisteredVendorRecord,
              { params: query }
            ),
          Admin: () =>
            this.http.post(
              `/api/v2/admin/clientRegisteredVendor/one`,
              ClientRegisteredVendorRecord,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public saveClientRegisteredVendorsHandleCompliance(
    ClientRegisteredVendorRecords: ClientRegisteredVendor[],
    query = {}
  ) {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.post(
              `/api/v2/admin/clientRegisteredVendor/saveClientRegisteredVendorHandleCompliance/many`,
              ClientRegisteredVendorRecords,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public attemptToConvertViveVendorToClientRegisteredVendor(
    Vendor: Vendor,
    query = {}
  ): Observable<ClientRegisteredVendor> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Client: () =>
            this.http.post<ClientRegisteredVendor>(
              `/api/v2/company/${this.AuthService.getUserCompanyId()}/clientRegisteredVendor/attemptToConvertViveVendorToClientRegisteredVendor`,
              Vendor,
              { params: query }
            ),
          Admin: () =>
            this.http.post<ClientRegisteredVendor>(
              `/api/v2/admin/clientRegisteredVendor/attemptToConvertViveVendorToClientRegisteredVendor`,
              Vendor,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }

  public sendClientRegisteredVendorInvitation(
    query = {}
  ): Observable<ClientRegisteredVendor> {
    return this.AuthService.getUserType().pipe(
      take(1),
      concatMap((userType: string) => {
        const userTypeToObservableMap: {
          [key: string]: () => Observable<any>;
        } = {
          Admin: () =>
            this.http.get<ClientRegisteredVendor>(
              `/api/v2/admin/clientRegisteredVendor/sendClientRegisteredVendorInvitation`,
              { params: query }
            )
        };

        if (userTypeToObservableMap[userType]) {
          return userTypeToObservableMap[userType]();
        }

        throw this.ToastService.addErrorToast('Error', 'Invalid permission.', {
          returnError: true
        });
      })
    );
  }
}
